// RefusalLine — the ONE line a write surface shows for a refused save (ADR-084 §6), in the same slot of every
// container (above the fields; a staged drawer's body first): refused (danger, dismissible) · stale (warning, ONE
// act — Load the latest, busy while the fresh read comes) · gone (danger, ONE act — Close, the container's leave)
// · the latest showing (info, dismissible; it takes the focus the vanished act held). Nothing is a toast, nothing
// navigates. The state is useRefusalLine's; the words errors.json (the code) + common:forms.

import { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '../../ui/Alert.js';
import type { RefusalLineState } from './useRefusalLine.js';

export interface RefusalLineProps {
  line: RefusalLineState;
  /** The latest showing with the typed values kept (a form) — `false` for a draft that was replaced (a staged draft). */
  kept?: boolean;
  /** The container's leave for the gone line's Close — without the discard prompt. */
  onClose?: () => void;
  className?: string;
}

export function RefusalLine({ line, kept = true, onClose, className }: RefusalLineProps) {
  const { t } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const n = line.notice;
  // After Load the latest the act's button is gone with the stale line: the latest line takes the focus it held,
  // so the keyboard keeps a place and the reader hears what happened — only on that transition, and only when the
  // focus did fall with the act (a person who moved on to a field meanwhile is not pulled back). A layout effect: the
  // focus lands in the same commit that shows the line, never a beat after it.
  const latestRef = useRef<HTMLDivElement>(null);
  const prevKind = useRef<string | null>(null);
  useLayoutEffect(() => {
    const kind = n?.kind ?? null;
    if (kind === 'latest' && prevKind.current === 'stale' && (document.activeElement === null || document.activeElement === document.body)) latestRef.current?.focus();
    prevKind.current = kind;
  }, [n?.kind]);

  if (!n) return null;
  switch (n.kind) {
    case 'refused':
      return (
        <Alert variant="danger" onDismiss={line.dismiss} className={className}>
          {n.message}
        </Alert>
      );
    case 'stale':
      return (
        <Alert
          variant="warning"
          className={className}
          action={line.canReload ? { label: t('forms.loadLatest'), onClick: () => void line.loadLatest(), busy: n.busy } : undefined}
        >
          {tErrors('STALE_WRITE')}
        </Alert>
      );
    case 'gone':
      return (
        <Alert variant="danger" className={className} action={onClose ? { label: t('actions.close'), onClick: onClose } : undefined}>
          {t('forms.gone')}
        </Alert>
      );
    case 'latest':
      return (
        <div ref={latestRef} tabIndex={-1} className={`outline-none ${className ?? ''}`}>
          <Alert variant="info" onDismiss={line.dismiss}>
            {t(kept ? 'forms.showingLatest' : 'forms.showingLatestPlain')}
          </Alert>
        </div>
      );
  }
}
